/**
 * A drawn track's breadcrumbs: lifted symbols in 3D, circles in 2D (#820 step 8).
 *
 * Two facts shape this, both verified 2026-09-13 rather than taken from docs:
 *
 *  - A CIRCLE cannot be lifted to an altitude. The installed mapbox-gl 3.25.0
 *    style spec gives circle-elevation-reference only none | hd-road-markup,
 *    and has no circle-z-offset. A lifted breadcrumb is therefore a SYMBOL.
 *
 *  - symbol-z-offset is in RENDERED metres. In the browser, a dot lifted by
 *    altitude x exaggeration sat on the Fire Boss model and one lifted by the
 *    altitude alone sat well below it. That is the OPPOSITE of the tail and
 *    track lines, where line-elevation-ground-scale 1 applies the exaggeration
 *    for us (affLineElevation). Symbols have no ground-scale, so the dots have
 *    to be told it. Do not "tidy" the two onto one formula.
 *
 * Hover also carries over: an invisible elevated symbol is still hit-tested,
 * and a larger one is centred on the visible dot, so the #812 pattern — a small
 * dot for looks, a bigger invisible target for the pointer — works lifted too.
 * But icon-ignore-placement: true takes a symbol out of hit-testing entirely,
 * so it is never set here.
 */

import { hasCompleteHeights } from '../liveLayers/providers/affLineElevation';

/** Breadcrumb dot size and outline (#812). */
export const BREADCRUMB_RADIUS = 3.5;
export const BREADCRUMB_STROKE_WIDTH = 1;

/**
 * How close the pointer has to get, as opposed to how big the dot looks.
 *
 * A 3.5px dot is a precision target with a mouse and an unreasonable one on a
 * map the user is also panning. Mapbox hit-tests a circle at its rendered
 * radius, so the only way to separate reach from appearance is a second,
 * invisible circle over the top (#812).
 */
export const BREADCRUMB_HIT_RADIUS = 12;

/** The shared dot image the lifted breadcrumbs are drawn with, tinted per track. */
export const BREADCRUMB_ICON_ID = 'aff-breadcrumb-dot';

/** Pixel size of the dot image, and the radius of the dot inside it. */
const DOT_IMAGE_SIZE = 32;
const DOT_IMAGE_RADIUS = 12;

/**
 * icon-size is a multiple of the image. The image's dot is DOT_IMAGE_RADIUS
 * across in radius, so these scale it to the same radii the circles use.
 */
const DOT_ICON_SIZE = BREADCRUMB_RADIUS / DOT_IMAGE_RADIUS;
const HIT_ICON_SIZE = BREADCRUMB_HIT_RADIUS / DOT_IMAGE_RADIUS;

export interface BreadcrumbLayerInput {
  sourceId: string;
  pointLayerId: string;
  hitLayerId: string;
  color: string;
  halo: string;
  opacity: number;
  /** null in 2D; in 3D, the terrain exaggeration the dots must be lifted by. */
  lift: { exaggeration: number } | null;
}

export const breadcrumbLayerSpecs = (input: BreadcrumbLayerInput): any[] => {
  const { sourceId, pointLayerId, hitLayerId, color, halo, opacity, lift } = input;

  if (!lift) {
    return [
      {
        id: pointLayerId,
        type: 'circle',
        source: sourceId,
        paint: {
          'circle-radius': BREADCRUMB_RADIUS,
          'circle-color': color,
          // The same halo the line uses, so a breadcrumb stays visible over its
          // own track and over whatever the basemap puts beneath it.
          'circle-stroke-color': halo,
          'circle-stroke-width': BREADCRUMB_STROKE_WIDTH,
          'circle-opacity': opacity,
          'circle-stroke-opacity': opacity,
        },
      },
      {
        // The invisible target that actually catches the pointer. Above the dots
        // so it wins the hit test, and carrying no paint of its own.
        id: hitLayerId,
        type: 'circle',
        source: sourceId,
        paint: {
          'circle-radius': BREADCRUMB_HIT_RADIUS,
          'circle-opacity': 0,
          'circle-stroke-width': 0,
        },
      },
    ];
  }

  // RENDERED metres: altitude times exaggeration. See the header — not like lines.
  const zOffset = ['*', ['get', 'altM'], lift.exaggeration];
  const liftedLayout = {
    'icon-image': BREADCRUMB_ICON_ID,
    // Every dot is drawn; a dense track would otherwise lose some to collision.
    'icon-allow-overlap': true,
    // icon-ignore-placement is deliberately NOT set: it removes the symbol from
    // hit-testing, and the dots must hover.
    'symbol-z-elevate': true,
    'symbol-elevation-reference': 'sea',
  };

  return [
    {
      id: pointLayerId,
      type: 'symbol',
      source: sourceId,
      layout: { ...liftedLayout, 'icon-size': DOT_ICON_SIZE },
      paint: {
        'symbol-z-offset': zOffset,
        'icon-color': color,
        'icon-halo-color': halo,
        'icon-halo-width': BREADCRUMB_STROKE_WIDTH,
        'icon-opacity': opacity,
      },
    },
    {
      id: hitLayerId,
      type: 'symbol',
      source: sourceId,
      layout: { ...liftedLayout, 'icon-size': HIT_ICON_SIZE },
      paint: {
        'symbol-z-offset': zOffset,
        'icon-opacity': 0,
      },
    },
  ];
};

/**
 * The dot, as an SDF image so one image can be tinted with any track's colour
 * and halo through paint.
 *
 * Built as raw RGBA rather than on a canvas: it has to run where there is no
 * canvas (the test environment), and mapbox accepts { width, height, data }.
 * The alpha channel is a signed distance field in mapbox's convention — about
 * 191 at the edge, rising inside and falling outside — which is what lets a
 * halo be drawn round it.
 */
export const breadcrumbDotImage = (): { width: number; height: number; data: Uint8ClampedArray } => {
  const size = DOT_IMAGE_SIZE;
  const data = new Uint8ClampedArray(size * size * 4);
  const centre = (size - 1) / 2;

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const distance = Math.hypot(x - centre, y - centre);
      const i = (y * size + x) * 4;
      data[i] = 0;
      data[i + 1] = 0;
      data[i + 2] = 0;
      data[i + 3] = Math.max(0, Math.min(255, Math.round(191 + (DOT_IMAGE_RADIUS - distance) * 32)));
    }
  }

  return { width: size, height: size, data };
};

/**
 * Whether every breadcrumb has a real altitude in metres. A single gap means
 * the breadcrumbs stay flat: lifting the rest would draw heights that are part
 * guess. Only `altM` counts — `alt` is feet for display.
 */
export const hasBreadcrumbHeights = (positions: unknown[]): boolean =>
  hasCompleteHeights((positions ?? []).map((p) => (p as { altM?: unknown } | null)?.altM));
